import { Component, type ErrorInfo, type ReactNode } from "react";
import {
  formatStartupDiagnosticForDisplay,
  readStartupDiagnostic,
  recordFirstStartupDiagnosticWithCode,
} from "@/lib/startupDiagnostics";
import {
  dispatchStartupMountSignalOnce,
  useStartupMountSignalOnce,
} from "@/lib/startupMountSignal";
import { resetRecipmonServiceWorkerAndCaches } from "@/lib/serviceWorkerRecovery";

type Props = {
  children: ReactNode;
};

type State = {
  hasError: boolean;
  message: string | null;
  isAutoRecovering: boolean;
};

const DEFAULT_ERROR_MESSAGE =
  "The app hit an unexpected error. Please reload and try again.";

const AppErrorFallback = ({ message }: { message: string }) => {
  useStartupMountSignalOnce("error-boundary");
  const diagnosticHint = formatStartupDiagnosticForDisplay(readStartupDiagnostic());

  return (
    <div className="min-h-screen w-full bg-background text-foreground flex items-center justify-center p-4">
      <div className="w-full max-w-lg rounded-2xl border-2 border-foreground bg-card p-5 shadow-[6px_6px_0px_0px_rgba(0,0,0,1)]">
        <h1 className="text-lg font-black">Recipmon failed to load</h1>
        <p className="mt-2 text-sm opacity-90">{message || DEFAULT_ERROR_MESSAGE}</p>
        {diagnosticHint ? (
          <p className="mt-2 text-xs opacity-75">
            Diagnostic: {diagnosticHint}
          </p>
        ) : null}
        <div className="mt-4 flex flex-wrap gap-2">
          <button
            type="button"
            className="inline-flex h-9 items-center justify-center rounded-md border-2 border-foreground px-3 text-xs font-black"
            onClick={() => resetRecipmonServiceWorkerAndCaches({ reloadIfNoChanges: true, reloadParam: 'err_sw_reset' })}
          >
            Reload App
          </button>
          <a
            href="/recipmontelegram/"
            className="inline-flex h-9 items-center justify-center rounded-md border-2 border-foreground px-3 text-xs font-black"
          >
            Open Mini App Route
          </a>
        </div>
      </div>
    </div>
  );
};

export class AppErrorBoundary extends Component<Props, State> {
  state: State = {
    hasError: false,
    message: null,
    isAutoRecovering: false,
  };

  static getDerivedStateFromError(error: unknown): Partial<State> {
    const message = error instanceof Error ? error.message : DEFAULT_ERROR_MESSAGE;
    return {
      hasError: true,
      message: message || DEFAULT_ERROR_MESSAGE,
    };
  }

  async componentDidMount() {
    if (this.state.hasError) {
      await this.attemptAutoRecovery();
    }
  }

  async componentDidUpdate(prevProps: Props, prevState: State) {
    if (this.state.hasError && !prevState.hasError) {
      await this.attemptAutoRecovery();
    }
  }

  async attemptAutoRecovery() {
    if (typeof window === "undefined" || this.state.isAutoRecovering) return;
    
    try {
      const url = new URL(window.location.href);
      if (url.searchParams.get("err_sw_reset") === "1") {
        return;
      }
      
      this.setState({ isAutoRecovering: true });
      console.info("[error-boundary] Attempting automatic cache recovery...");
      
      await resetRecipmonServiceWorkerAndCaches({
        reloadIfNoChanges: true,
        reloadParam: "err_sw_reset"
      });
    } catch (e) {
      console.error("[error-boundary] Auto recovery failed", e);
      this.setState({ isAutoRecovering: false });
    }
  }

  componentDidCatch(error: unknown, info: ErrorInfo): void {
    recordFirstStartupDiagnosticWithCode("runtime_bootstrap", error);
    dispatchStartupMountSignalOnce("error-boundary-catch");
    console.error("Unhandled application error:", error, info);
  }

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    if (this.state.isAutoRecovering) {
      return (
        <div className="min-h-screen w-full bg-background flex flex-col items-center justify-center p-4">
          <div className="w-full max-w-sm rounded-2xl border-2 border-foreground bg-card p-6 shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] text-center space-y-4">
            <div className="w-12 h-12 border-4 border-foreground border-t-red-500 rounded-full animate-spin mx-auto"></div>
            <h2 className="text-lg font-black animate-pulse">Auto-recovering...</h2>
            <p className="text-sm opacity-80">Clearing stale caches and reloading the app.</p>
          </div>
        </div>
      );
    }

    return <AppErrorFallback message={this.state.message || DEFAULT_ERROR_MESSAGE} />;
  }
}
